<template>
  <div
    class="avatar"
    :class="[`avatar--${size}`, { 'avatar--ring': ring-3 }, colorClass]"
    :style="explicitSize ? { width: explicitSize, height: explicitSize, fontSize: 'inherit' } : null"
    :title="fullName || undefined"
  >
    <img v-if="resolvedImageUrl" :src="resolvedImageUrl" :alt="fullName" class="avatar__img" />
    <span v-else class="avatar__initials">{{ initials }}</span>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  user: { type: Object, default: () => ({}) },
  name: { type: String, default: '' },
  imageUrl: { type: String, default: '' },
  size: { type: String, default: 'md' },
  pxSize: { type: String, default: '' },
  ring: { type: Boolean, default: true },
  color: {
    type: String,
    default: null,
    validator: (v) => v === null || ['blue', 'amber', 'rose', 'violet', 'teal', 'red'].includes(v),
  },
});

const colorClass = computed(() => (props.color ? `avatar--color-${props.color}` : null));

const explicitSize = computed(() => props.pxSize || '');

const resolvedImageUrl = computed(() => props.imageUrl || props.user?.profile_photo_url || '');

const initials = computed(() => {
  const trimmedName = props.name?.trim();
  if (trimmedName) {
    return (
      trimmedName
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0]?.toUpperCase() || '')
        .join('') || '?'
    );
  }
  const u = props.user || {};
  const first = (u.preferred_first_name || u.first_name || '').charAt(0);
  const last = (u.preferred_last_name || u.last_name || '').charAt(0);
  const combined = (first + last).toUpperCase();
  return combined || (u.username?.charAt(0).toUpperCase()) || '?';
});

const fullName = computed(() => {
  if (props.name?.trim()) return props.name.trim();
  const u = props.user || {};
  const composed = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return u.display_name || composed || u.username || '';
});
</script>

<style scoped>
.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  overflow: hidden;
  background: var(--p-gray-200);
  color: var(--p-gray-800);
  font-weight: 600;
  aspect-ratio: 1 / 1;
  user-select: none;
}

.avatar--ring {
  box-shadow: 0 0 0 2px white, 0 0 0 4px var(--p-gray-300);
}

.avatar--sm {
  width: 2rem;
  height: 2rem;
  font-size: 0.75rem;
}

.avatar--md {
  width: 3rem;
  height: 3rem;
  font-size: 1rem;
}

.avatar--lg {
  width: 5rem;
  height: 5rem;
  font-size: 1.5rem;
}

.avatar--xl {
  width: 6rem;
  height: 6rem;
  font-size: 1.75rem;
}

.avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.avatar__initials {
  line-height: 1;
}

.avatar--color-blue   { background: var(--p-blue-100,   #dbeafe); color: var(--p-blue-700,   #1d4ed8); }
.avatar--color-amber  { background: var(--p-amber-100,  #fef3c7); color: var(--p-amber-700,  #b45309); }
.avatar--color-rose   { background: var(--p-rose-100,   #ffe4e6); color: var(--p-rose-700,   #be123c); }
.avatar--color-violet { background: var(--p-violet-100, #ede9fe); color: var(--p-violet-700, #6d28d9); }
.avatar--color-teal   { background: var(--p-teal-100,   #ccfbf1); color: var(--p-teal-700,   #0f766e); }
.avatar--color-red    { background: var(--p-red-100,    #fee2e2); color: var(--p-red-700,    #b91c1c); }
</style>
